import React from 'react';

import { ArrowLeft, Search } from 'lucide-react';
import { NavLink } from 'react-router-dom';

/**
 * The hub's small repeated chrome, in one place.
 *
 * Each of these appeared three or four times across the archives and reading
 * views, hand-rolled and already slightly divergent. Collecting them is what
 * makes the design system enforceable here: a hairline is a hairline because
 * there is one definition of it, not because four files happened to agree.
 */

/* --- page header --------------------------------------------------------- */

interface PageHeaderProps {
  title: string;
  intro?: string;
}

/**
 * A section's opening. EB Garamond, left-aligned, no kicker above it.
 *
 * Left, not centred: DESIGN.md's layout is a sheet read from its left edge, and
 * a centred column of headings is the marketing-page shape the world refuses.
 * The No-Eyebrow Rule bans the small tracked label that used to sit above.
 */
export const PageHeader: React.FC<PageHeaderProps> = ({ title, intro }) => (
  <header className="max-w-measure">
    <h1 className="font-display text-step-4 leading-[1.02] tracking-[-0.02em] text-ink">{title}</h1>
    {intro && <p className="mt-4 text-step-0 text-ink-muted">{intro}</p>}
  </header>
);

/* --- back link ----------------------------------------------------------- */

interface BackLinkProps {
  to: string;
  label: string;
  /** Overrides the default spacing. The control's own look is fixed. */
  className?: string;
}

/**
 * A real link, never a history control. It always names a destination it can
 * point at, so it behaves the same whether the reader arrived by clicking through
 * or by pasting the URL — and it can be middle-clicked and previewed like any
 * other link, which `history.back()` cannot.
 */
export const BackLink: React.FC<BackLinkProps> = ({ to, label, className = 'mb-8' }) => (
  <NavLink
    to={to}
    className={`inline-flex items-center gap-1.5 font-ui text-step--1 text-ink-muted transition-colors duration-fast ease-sheet hover:text-ink ${className}`}
  >
    <ArrowLeft size={15} aria-hidden="true" />
    {label}
  </NavLink>
);

/* --- search -------------------------------------------------------------- */

interface SearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  label: string;
  /**
   * Overrides the default placement. The field's own look is fixed — only where it
   * sits is the caller's business. The news feed puts it in the header's right
   * column rather than under the heading, which is a layout decision, not a
   * licence to restyle the control.
   */
  className?: string;
}

/**
 * A hairline field, square but for the one 2px radius, on the sunk ground.
 *
 * The focus ring is apricot, which is one of the accent's few sanctioned uses —
 * DESIGN.md lists the primary action, the pointer on the terrain, the focus ring
 * and two hover states, and nothing else. It arrives as a border and a ring, not
 * as ink: the text stays `--page-ink` throughout.
 */
export const SearchField: React.FC<SearchFieldProps> = ({
  value,
  onChange,
  label,
  className = 'mt-10 max-w-md',
}) => (
  <div className={`relative ${className}`}>
    <Search
      size={16}
      aria-hidden="true"
      className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted"
    />
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={label}
      aria-label={label}
      className="w-full rounded-sheet border border-rule-strong bg-page-sunk py-2.5 pl-9 pr-4 font-ui text-step--1 text-ink placeholder:text-ink-muted focus:border-apricot focus:outline-none focus:ring-1 focus:ring-apricot"
    />
  </div>
);

/* --- group heading ------------------------------------------------------- */

/**
 * The rule above a group of rows — a year in the newsletter archive, a category
 * in the article library. The One Label Rule's single label style.
 */
export const GroupHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h2 className="border-b border-rule-strong pb-2 font-ui text-step--2 font-medium uppercase tracking-[0.18em] text-ink-muted">
    {children}
  </h2>
);

/* --- filter chips -------------------------------------------------------- */

export interface FilterChip {
  label: string;
  /** Null is the "everything" chip. */
  value: string | null;
}

interface FilterChipsProps {
  chips: FilterChip[];
  selected: string | null;
  onSelect: (value: string | null) => void;
}

/**
 * A horizontal run of filters, in the single label style.
 *
 * Not chips in the pill sense — DESIGN.md has no pill and no filled tag. The selected
 * one is marked by an underline and full-strength ink, which is the only distinction
 * the system needs and the same device the top bar uses for the current section.
 */
export const FilterChips: React.FC<FilterChipsProps> = ({ chips, selected, onSelect }) => (
  <div className="flex flex-wrap gap-x-6 gap-y-3">
    {chips.map((chip) => {
      const active = chip.value === selected;
      return (
        <button
          key={chip.label}
          type="button"
          onClick={() => onSelect(chip.value)}
          aria-current={active ? 'true' : undefined}
          className={`font-ui text-step--2 font-medium uppercase tracking-[0.18em] transition-colors duration-fast ease-sheet ${
            active
              ? 'text-ink underline decoration-1 underline-offset-4'
              : 'text-ink-muted hover:text-ink'
          }`}
        >
          {chip.label}
        </button>
      );
    })}
  </div>
);

/* --- empty state --------------------------------------------------------- */

export const EmptyNote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="py-16 text-step-0 text-ink-muted">{children}</p>
);
